import { motion } from 'framer-motion';
import { Mail, Phone, Linkedin, Instagram, Facebook, Youtube, MapPin } from 'lucide-react';
import { WordsPullUpMultiStyle } from '../components/AnimationComponents';

export const ContactSection = () => {
  const contactMethods = [
    {
      icon: Mail,
      label: 'Email',
      value: 'thehygienecompany.26@gmail.com',
      href: 'mailto:thehygienecompany.26@gmail.com',
      color: '#87CEEB'
    },
    {
      icon: Phone,
      label: 'WhatsApp',
      value: '+91 74177 09268',
      href: 'https://wa.me/917417709268',
      color: '#90EE90'
    },
    {
      icon: MapPin,
      label: 'Visit our Office',
      value: 'Ward No. 5, 442, Near Tehsil Jail Camp Road, Sitarganj, Udham Singh Nagar',
      href: 'https://www.google.com/maps/search/?api=1&query=Ward+No.+5+442+Tehsil+Jail+Camp+Road+Sitarganj+Udham+Singh+Nagar',
      color: '#F4A261'
    }
  ];

  const socialLinks = [
    {
      icon: Linkedin,
      name: 'LinkedIn',
      href: 'https://www.linkedin.com/company/the-hygiene-company/',
      color: '#0A66C2'
    },
    {
      icon: Instagram,
      name: 'Instagram',
      href: 'https://www.instagram.com/thehygienecompany.26?igsh=MWs0YzdqMml2cWtjMA==',
      color: '#E4405F'
    },
    {
      icon: Facebook,
      name: 'Facebook',
      href: 'https://www.facebook.com/share/1HSDGSDwJp/',
      color: '#1877F2'
    },
    {
      icon: Youtube,
      name: 'YouTube',
      href: 'https://youtube.com/@thehygienecompany?si=wCOCnY7RqAGjsYuo',
      color: '#FF0000'
    }
  ];

  return (
    <section id="contact" className="min-h-screen py-12 md:py-20 px-4 md:px-8" data-testid="contact-section">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12 md:mb-16">
          <WordsPullUpMultiStyle
            segments={[
              { text: "Let's", className: "text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-normal text-white drop-shadow-lg" },
              { text: "Connect", className: "text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-normal text-[#87CEEB] drop-shadow-lg" }
            ]}
            containerClassName="mb-6"
          />
          
          <p className="text-white/90 text-sm md:text-base max-w-2xl mx-auto drop-shadow">
            Have questions about our products or services? Want to partner with us for institutional hygiene solutions? We'd love to hear from you!
          </p>
        </div>

        {/* Contact Methods */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          {contactMethods.map((method, i) => (
            <motion.a
              key={i}
              href={method.href}
              target="_blank"
              rel="noopener noreferrer"
              initial={{ y: 30, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.2 }}
              whileHover={{ scale: 1.05, y: -5 }}
              className="bg-white/15 backdrop-blur-md border border-white/30 rounded-3xl p-8 hover:bg-white/25 transition-all duration-300"
            >
              <div className="flex items-start gap-4">
                <div 
                  className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: `${method.color}40` }}
                >
                  <method.icon className="w-7 h-7" style={{ color: method.color }} />
                </div>
                
                <div className="flex-1">
                  <h3 className="text-white font-bold text-lg mb-2 drop-shadow">
                    {method.label}
                  </h3>
                  <p className="text-white/90 text-sm break-all">
                    {method.value}
                  </p>
                </div>
              </div>
            </motion.a>
          ))}
        </div>

        {/* Social Links */}
        <motion.div
          initial={{ y: 30, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="bg-white/15 backdrop-blur-md border border-white/30 rounded-3xl p-8"
        >
          <h3 className="text-white text-xl font-bold text-center mb-8 drop-shadow">
            Follow Our Journey
          </h3>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {socialLinks.map((social, i) => (
              <motion.a
                key={i}
                href={social.href}
                target="_blank"
                rel="noopener noreferrer"
                initial={{ scale: 0.8, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.5 + i * 0.1 }}
                whileHover={{ scale: 1.1, y: -5 }}
                whileTap={{ scale: 0.95 }}
                className="flex flex-col items-center gap-3 p-6 rounded-2xl bg-white/10 hover:bg-white/20 border border-white/20 hover:border-white/40 transition-all duration-300"
              >
                <div 
                  className="w-12 h-12 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: `${social.color}40` }}
                >
                  <social.icon className="w-6 h-6" style={{ color: social.color }} />
                </div>
                <span className="text-white text-sm font-medium drop-shadow">
                  {social.name}
                </span>
              </motion.a>
            ))}
          </div>
        </motion.div>

        {/* Footer */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.8 }}
          className="mt-16 text-center"
        >
          <p className="text-white/90 text-sm mb-4 drop-shadow">
            "Clean Spaces. Healthy Lives. Better Future."
          </p>
          <p className="text-white/70 text-xs">
            © 2025 The Hygiene Company. All rights reserved.
          </p>
          <p className="text-white text-xs mt-2 drop-shadow">
            Building a healthier India, one space at a time.
          </p>
        </motion.div>
      </div>
    </section>
  );
};
